import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EmptyOperationalState from '../components/EmptyOperationalState';
import PaymentStatusBadge from '../components/PaymentStatusBadge';
import DataState from '../components/DataState';
import { useSession } from '../permissions/SessionContext';
import { hasPermissions } from '../permissions/check';
import { fetchOwnerDashboard } from '../api/members';
import { fetchAcademySummary } from '../api/academy';
import { CATEGORY_LABELS } from '../navigation';
import { colors, layout, radius } from '../design-system/tokens';
import AcademyPilotGuide from '../modules/academy/AcademyPilotGuide';

const num = (value) => Number(value || 0).toLocaleString('ko-KR');
const percent = (part, total) => {
  if (!Number(total)) return 0;
  return Math.max(0, Math.min(100, Math.round((Number(part || 0) / Number(total)) * 100)));
};

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

const queueTone = (severity) => {
  if (severity === 'high') return colors.status.danger;
  if (severity === 'mid') return colors.status.warning;
  return colors.status.neutral;
};

const severityLabel = {
  high: '긴급',
  mid: '주의',
  low: '관리',
};

const CARWASH_QUEUE = [
  {
    key: 'payment_failed',
    title: '결제 확인',
    countKey: 'paymentFailed',
    severity: 'high',
    summary: '월 구독 결제',
    description: '결제가 멈춘 회원을 먼저 확인해 주세요. 안내나 재시도 전 최근 이력을 함께 봅니다.',
    cta: '결제 실패 회원 보기',
  },
  {
    key: 'cancelling',
    title: '구독 변화',
    countKey: 'cancellationRequested',
    severity: 'mid',
    summary: '해지 · 만료 흐름',
    description: '해지 예정이거나 구독 상태가 바뀌는 회원을 살펴보고 필요한 연락을 정리합니다.',
    cta: '구독 변경 확인',
  },
  {
    key: 'no_vehicle',
    title: '회원 정보 보완',
    countKey: 'noVehicle',
    severity: 'low',
    summary: '이용 준비 정보',
    description: '멤버십 이용에 필요한 기본 정보가 비어 있는 회원을 확인합니다.',
    cta: '보완 대상 보기',
  },
  {
    key: 'inactive30',
    title: '재방문 케어',
    countKey: 'inactive30',
    severity: 'low',
    summary: '최근 이용 공백',
    description: '방문 흐름이 끊긴 회원을 모아 재방문 안내가 필요한지 검토합니다.',
    cta: '케어 대상 보기',
  },
];

const ACADEMY_QUEUE = [
  {
    key: 'students',
    title: '수강생 확인',
    countKey: 'paused',
    severity: 'mid',
    description: '휴원 중인 수강생을 확인하고 재등록 가능성을 점검해 보세요.',
    cta: '수강생 보기',
    path: '/owner-v2/students',
  },
  {
    key: 'attendance',
    title: '출결 확인',
    countKey: 'attendanceReview',
    severity: 'low',
    description: '출결 확인이 필요한 수업을 점검해 주세요.',
    cta: '출결 보기',
    path: '/owner-v2/attendance',
  },
];

const OperatingQueueCard = ({ item, count, onClick }) => {
  const tone = queueTone(item.severity);
  const active = Number(count) > 0;
  const status = item.severity === 'high' ? 'failed' : item.severity === 'mid' ? 'warning' : 'neutral';

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${item.title} ${num(count)}건 확인으로 이동`}
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'block',
        textAlign: 'left',
        borderRadius: `${radius.md}px`,
        '&:focus-visible': {
          outline: `2px solid ${colors.brand.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <Paper
        sx={{
          position: 'relative',
          height: '100%',
          minHeight: 148,
          p: { xs: 2, sm: 2.25 },
          overflow: 'hidden',
          borderColor: active ? tone.border : colors.border.muted,
          '&:hover': { bgcolor: colors.canvas.subtle },
        }}
      >
        <Stack spacing={1.4} sx={{ height: '100%', minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <PaymentStatusBadge
                label={severityLabel[item.severity]}
                status={status}
                sx={{
                  height: 24,
                  bgcolor: active ? undefined : colors.status.neutral.bg,
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{ mt: 1, fontSize: 15, fontWeight: 800, minWidth: 0 }}
              >
                {item.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, flexShrink: 0 }}>
              <Typography variant="h5" sx={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}>
                {num(count)}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                건
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.text.muted, fontWeight: 800 }}
            >
              {item.summary}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.45,
                color: colors.text.secondary,
                fontSize: 13,
                minHeight: { sm: 40 },
                overflowWrap: 'anywhere',
              }}
            >
              {item.description}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'inline-flex', color: colors.brand.primary, fontWeight: 800 }}
          >
            {item.cta} →
          </Typography>
        </Stack>
      </Paper>
    </ButtonBase>
  );
};

const MetricSummaryCard = ({ label, value, caption, trend }) => (
  <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: colors.text.secondary, fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{ mt: 1, fontSize: { xs: 21, sm: 24 }, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    {(caption || trend) && (
      <Typography
        variant="caption"
        sx={{ color: trend === 'up' ? colors.status.success.fg : colors.text.muted }}
      >
        {caption}
      </Typography>
    )}
  </Paper>
);

const FocusMiniMetric = ({ label, value, status = 'neutral', caption }) => (
  <Box
    sx={{
      minWidth: 0,
      p: 1.5,
      borderRadius: `${radius.sm}px`,
      bgcolor: colors.surface,
      border: `1px solid ${colors.border.soft}`,
    }}
  >
    <PaymentStatusBadge label={label} status={status} sx={{ height: 22 }} />
    <Typography
      variant="h6"
      sx={{ mt: 1, fontSize: 20, fontVariantNumeric: 'tabular-nums' }}
    >
      {num(value)}건
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        {caption}
      </Typography>
    )}
  </Box>
);

const DashboardFocusHero = ({ totalQueue, urgentQueue, summary, todayWash }) => {
  const isCalm = Number(urgentQueue) === 0;

  return (
    <Paper
      sx={{
        p: { xs: 2.25, sm: 2.75 },
        mb: layout.cardGap,
        minWidth: 0,
        bgcolor: isCalm ? colors.status.success.bg : colors.brand.primarySoft,
        borderColor: isCalm ? colors.status.success.border : colors.brand.primarySoftBorder,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(360px, 0.9fr)' },
          gap: { xs: 2, md: 3 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: isCalm ? colors.status.success.fg : colors.brand.primary,
              fontWeight: 900,
            }}
          >
            오늘의 운영
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mt: 0.75,
              fontSize: { xs: 28, sm: 34 },
              lineHeight: 1.12,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {isCalm ? '우선 확인할 일이 없어요' : `우선 확인 ${num(urgentQueue)}건`}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, color: colors.text.secondary, maxWidth: 560 }}
          >
            전체 운영 포커스 {num(totalQueue)}건 · 오늘 이용 {num(todayWash)}건입니다.
            결제와 구독 변화처럼 회원 경험에 바로 영향을 주는 항목부터 확인하세요.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1,
          }}
        >
          <FocusMiniMetric
            label="결제 확인"
            value={summary?.paymentFailed}
            status={Number(summary?.paymentFailed) > 0 ? 'failed' : 'neutral'}
            caption="자동결제"
          />
          <FocusMiniMetric
            label="구독 변화"
            value={summary?.cancellationRequested}
            status={Number(summary?.cancellationRequested) > 0 ? 'warning' : 'neutral'}
            caption="해지 예약"
          />
          <FocusMiniMetric
            label="정보 보완"
            value={summary?.noVehicle}
            status="neutral"
            caption="필수 정보"
          />
        </Box>
      </Box>
    </Paper>
  );
};

const FlowMetric = ({ label, value, caption, tone = 'neutral' }) => {
  const palette =
    tone === 'danger'
      ? colors.status.danger
      : tone === 'success'
        ? colors.status.success
        : tone === 'warning'
          ? colors.status.warning
          : colors.status.neutral;

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: `${radius.sm}px`,
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <Typography variant="caption" sx={{ color: palette.fg, fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ mt: 0.65, fontSize: 20, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const HealthRow = ({ label, value, caption, tone = 'info' }) => {
  const palette =
    tone === 'success'
      ? colors.status.success
      : tone === 'warning'
        ? colors.status.warning
        : colors.status.info;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            {caption}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: palette.fg, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}
        >
          {value}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 8,
          borderRadius: 99,
          bgcolor: colors.canvas.inset,
          '& .MuiLinearProgress-bar': {
            borderRadius: 99,
            bgcolor: palette.fg,
          },
        }}
      />
    </Box>
  );
};

const OperationsInsightPanel = ({ summary }) => {
  const totalMembers = Number(summary?.totalMembers || 0);
  const activeSubscriptions = Number(summary?.activeSubscriptions || 0);
  const completedInfo = Math.min(Number(summary?.vehicleCount || 0), totalMembers);
  const recentlyActiveMembers = Math.max(0, totalMembers - Number(summary?.inactive30 || 0));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.15fr) minmax(280px, 0.85fr)' },
        gap: layout.cardGap,
        mt: layout.cardGap,
      }}
    >
      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.35, sm: 1 }}
          sx={{
            alignItems: { xs: 'flex-start', sm: 'baseline' },
            justifyContent: 'space-between',
            mb: 1.5,
            minWidth: 0,
          }}
        >
          <Typography variant="subtitle2">이번 달 운영 흐름</Typography>
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            이번 달 회원과 구독 흐름을 한 번에 봅니다.
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 132px), 1fr))',
            gap: 1,
          }}
        >
          <FlowMetric
            label="신규 회원"
            value={`${num(summary?.newMembersThisMonth)}명`}
            caption="이번 달"
            tone="success"
          />
          <FlowMetric
            label="활성 구독"
            value={`${num(activeSubscriptions)}건`}
            caption="현재 이용 중"
            tone="success"
          />
          <FlowMetric
            label="구독 변화"
            value={`${num(summary?.cancellationRequested)}건`}
            caption="해지 예약"
            tone={Number(summary?.cancellationRequested) > 0 ? 'warning' : 'neutral'}
          />
          <FlowMetric
            label="결제 확인"
            value={`${num(summary?.paymentFailed)}건`}
            caption="우선 확인"
            tone={Number(summary?.paymentFailed) > 0 ? 'danger' : 'neutral'}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
        <Typography variant="subtitle2">회원 경험 점검</Typography>
        <Typography variant="caption" sx={{ color: colors.text.muted }}>
          가입 후 실제 이용까지 막히는 지점을 빠르게 봅니다.
        </Typography>
        <Stack spacing={1.75} sx={{ mt: 1.75 }}>
          <HealthRow
            label="구독 전환율"
            value={percent(activeSubscriptions, totalMembers)}
            caption={`${num(activeSubscriptions)} / ${num(totalMembers)}명`}
            tone="success"
          />
          <HealthRow
            label="필수 정보 완성"
            value={percent(completedInfo, totalMembers)}
            caption={`${num(completedInfo)} / ${num(totalMembers)}건`}
            tone="info"
          />
          <HealthRow
            label="재방문 신호"
            value={percent(recentlyActiveMembers, totalMembers)}
            caption={`30일 미이용 ${num(summary?.inactive30)}명`}
            tone={Number(summary?.inactive30) > 0 ? 'warning' : 'success'}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

const RecentUsageTable = ({ rows = [] }) => (
  <Paper sx={{ overflow: 'hidden', minWidth: 0 }}>
    <Box
      sx={{
        px: { xs: 2, sm: 2.25 },
        py: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.border.soft}`,
      }}
    >
      <Typography variant="subtitle2">최근 이용 내역</Typography>
      <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
        최근 5건
      </Typography>
    </Box>
    {rows.length ? (
      <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell>시간</TableCell>
              <TableCell>식별 정보</TableCell>
              <TableCell>회원</TableCell>
              <TableCell>결과</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.usedAt}-${row.licensePlate}`}>
                <TableCell>{row.usedAt}</TableCell>
                <TableCell>{row.licensePlate}</TableCell>
                <TableCell>{row.memberName}</TableCell>
                <TableCell>
                  <PaymentStatusBadge label={row.resultLabel || '성공'} status="success" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: colors.text.muted }}>
          아직 표시할 최근 이용 내역이 없어요.
        </Typography>
      </Box>
    )}
  </Paper>
);

const SideRail = ({ totalQueue, urgentQueue }) => (
  <Stack spacing={layout.cardGap} sx={{ minWidth: 0 }}>
    <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: colors.text.secondary, fontWeight: 700 }}>
        정산 데이터
      </Typography>
      <Typography variant="h5" sx={{ mt: 1, fontSize: 21 }}>
        연결 예정
      </Typography>
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        입금 예정일과 실입금액은 first-party 정산 API가 연결된 뒤 표시합니다.
      </Typography>
    </Paper>
    <Paper
      sx={{
        p: { xs: 2, sm: layout.cardPadding },
        minWidth: 0,
        bgcolor: colors.brand.primarySoft,
        borderColor: colors.brand.primarySoftBorder,
      }}
    >
      <Typography variant="caption" sx={{ color: colors.brand.primary, fontWeight: 800 }}>
        운영 포커스 기준
      </Typography>
      <Typography variant="h5" sx={{ mt: 1, fontSize: 21 }}>
        {num(totalQueue)}건
      </Typography>
      <Typography variant="caption" sx={{ color: colors.text.secondary }}>
        우선 확인 {num(urgentQueue)}건을 먼저 보고, 남은 정보 보완과 재방문 케어를 이어서 확인합니다.
      </Typography>
    </Paper>
  </Stack>
);

const CarwashDashboard = ({ session }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchOwnerDashboard(getScopedParams(session)));
    } catch (err) {
      setError(err?.response?.data?.message || '현황을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => data?.summary ?? {}, [data?.summary]);
  const queue = useMemo(
    () =>
      CARWASH_QUEUE.map((item) => ({
        ...item,
        count: summary[item.countKey] ?? 0,
      })),
    [summary],
  );
  const totalQueue = queue.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const urgentQueue = queue
    .filter((item) => item.severity === 'high' || item.severity === 'mid')
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
  const goQueue = (item) => {
    if (item.path) navigate(item.path);
    else navigate(`/owner-v2/members?filter=${encodeURIComponent(item.filterKey || item.key)}`);
  };

  return (
    <Box>
      <DataState loading={loading} error={error} onRetry={load}>
        <DashboardFocusHero
          totalQueue={totalQueue}
          urgentQueue={urgentQueue}
          summary={summary}
          todayWash={data?.todayWash}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', mb: 1.75, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant="h6">운영 포커스</Typography>
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            전체 {num(totalQueue)}건 · 우선 확인 {num(urgentQueue)}건
          </Typography>
        </Stack>
        {totalQueue > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 252px), 1fr))',
              gap: layout.cardGap,
            }}
          >
            {queue.map((item) => (
              <OperatingQueueCard
                key={item.key}
                item={item}
                count={item.count}
                onClick={() => goQueue(item)}
              />
            ))}
          </Box>
        ) : (
          <EmptyOperationalState
            variant="stable"
            title="오늘은 집중해서 볼 항목이 없어요"
            description="결제, 구독 변화, 회원 정보, 재방문 케어처럼 회원 경험에 영향이 큰 항목만 이곳에 모아둘게요."
          />
        )}

        <Typography variant="h6" sx={{ mt: layout.sectionGap, mb: 1.75 }}>
          매장 현황
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: layout.cardGap,
          }}
        >
          <MetricSummaryCard label="오늘 이용" value={`${num(data?.todayWash)}건`} caption="오늘 00시부터" />
          <MetricSummaryCard label="활성 구독" value={`${num(summary.activeSubscriptions)}건`} caption={`이번 달 신규 ${num(summary.newMembersThisMonth)}명`} trend="up" />
          <MetricSummaryCard label="연결 회원" value={`${num(summary.totalMembers)}명`} caption={`필수 정보 ${num(summary.vehicleCount)}건`} />
          <MetricSummaryCard label="30일 이용" value={`${num(summary.recentWashCount)}건`} caption="최근 30일" />
        </Box>

        <OperationsInsightPanel summary={summary} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 300px' },
            gap: layout.cardGap,
            mt: layout.cardGap,
          }}
        >
          <RecentUsageTable rows={data?.recentUsage ?? []} />
          <SideRail totalQueue={totalQueue} urgentQueue={urgentQueue} />
        </Box>
      </DataState>
    </Box>
  );
};

const AcademyDashboard = ({ session }) => {
  const navigate = useNavigate();
  const canReadTuition = hasPermissions(session, ['tuition.read']);
  const canReadParents = hasPermissions(session, ['parents.read']);
  const canUseAttendance = hasPermissions(session, ['attendance.read']);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchAcademySummary(getScopedParams(session)));
    } catch (err) {
      setError(err?.response?.data?.message || '현황을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const queue = ACADEMY_QUEUE.map((item) => ({
    ...item,
    count:
      item.countKey === 'paused'
        ? data?.students?.paused ?? 0
        : data?.[item.countKey] ?? 0,
  }));
  const totalQueue = queue.reduce((sum, item) => sum + Number(item.count || 0), 0);

  return (
    <Box>
      <DataState loading={loading} error={error} onRetry={load}>
        <AcademyPilotGuide
          summary={data}
          canReadParents={canReadParents}
          canUseAttendance={canUseAttendance}
          onNavigate={navigate}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', mb: 1.75, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant="h6">오늘 처리할 일</Typography>
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            전체 {num(totalQueue)}건
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 252px), 1fr))',
            gap: layout.cardGap,
          }}
        >
          {queue.map((item) => (
            <OperatingQueueCard
              key={item.key}
              item={item}
              count={item.count}
              onClick={() => navigate(item.path)}
            />
          ))}
        </Box>

        <Typography variant="h6" sx={{ mt: layout.sectionGap, mb: 1.75 }}>
          학원 현황
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: layout.cardGap,
          }}
        >
          <MetricSummaryCard label="재원생" value={`${num(data?.students?.active)}명`} caption={`휴원 ${num(data?.students?.paused)} · 퇴원 ${num(data?.students?.withdrawn)}`} />
          <MetricSummaryCard label="운영 반" value={`${num(data?.activeClasses)}개`} />
          {canReadTuition && (
            <MetricSummaryCard label="자동결제 등록" value={`${num(data?.tuition?.activeBillings)}건`} />
          )}
          {canReadTuition && (
            <MetricSummaryCard label="이번 달 예상 수강료" value={`${num(data?.tuition?.monthlyExpectedAmount)}원`} caption="active 자동결제 합계" />
          )}
        </Box>
      </DataState>
    </Box>
  );
};

const MetricsSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
      gap: layout.cardGap,
    }}
  >
    {Array.from({ length: 4 }).map((_, i) => (
      <Paper key={i} sx={{ p: 2 }}>
        <Skeleton variant="text" width="50%" height={20} />
        <Skeleton variant="text" width="70%" height={38} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="60%" height={18} />
      </Paper>
    ))}
  </Box>
);

const ComingSoonDashboard = ({ session }) => (
  <Box>
    <EmptyOperationalState
      variant="preparing"
      title={`${CATEGORY_LABELS[session.store.category] || session.store.category} 대시보드는 준비 중이에요`}
      description="같은 콘솔 구조 위에 업종별 운영 데이터가 연결되면 이곳에서 오늘 처리할 일을 확인할 수 있어요."
    />
    <Box sx={{ mt: 2 }}>
      <MetricsSkeleton />
    </Box>
  </Box>
);

const Dashboard = () => {
  const { session } = useSession();
  const category = session.store.category;

  if (category === 'academy') return <AcademyDashboard session={session} />;
  if (category === 'carwash') return <CarwashDashboard session={session} />;
  return <ComingSoonDashboard session={session} />;
};

export default Dashboard;
